import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import webhookSubmissionService from '../../../services/webhookSubmissionService';

const SimpleContactForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    companyName: '',
    tradeType: ''
  });
  
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const tradeOptions = [
    { value: 'hvac', label: 'HVAC Services' },
    { value: 'plumbing', label: 'Plumbing Services' },
    { value: 'electrical', label: 'Electrical Services' },
    { value: 'multi-trade', label: 'Multi-Trade Contracting' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (submitError) {
      setSubmitError('');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Validate all 5 required fields
    if (!formData?.fullName || !formData?.email || !formData?.phone || 
        !formData?.companyName || !formData?.tradeType) {
      setSubmitError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Properly map form data to database schema
      const submissionData = {
        fullName: formData?.fullName?.trim(),
        email: formData?.email?.trim()?.toLowerCase(),
        phone: formData?.phone?.trim(),
        companyName: formData?.companyName?.trim(),
        tradeSpecialty: formData?.tradeType, // Correct mapping to database field
        formSource: 'simple_contact_form',
        submissionType: 'lead'
      };

      console.log('Submitting simple contact form:', submissionData);
      
      const { data, error } = await webhookSubmissionService?.submitHeroForm(submissionData);

      if (error) {
        console.error('Form submission error:', error);
        setSubmitError(error?.message || 'Failed to submit form. Please try again.');
        return;
      }

      console.log('Form submitted successfully:', data);

      // Clear form and show success
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        companyName: '',
        tradeType: ''
      });
      
      setSubmitSuccess(true);
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);

    } catch (error) {
      console.error('Network error during form submission:', error);
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state display
  if (submitSuccess) {
    return (
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-8">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <Icon name="CheckCircle" size={32} className="text-green-600" />
                </div>
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Thank You for Your Interest!
              </h2>
              
              <p className="text-gray-600 mb-6">
                We have received your information and will contact you within 24 hours 
                to discuss how ProConnect can help grow your business.
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Next Steps:</strong> Our team will review your trade specialty 
                  and contact you to schedule a brief consultation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Get Started with ProConnect
          </h2>
          <p className="text-lg text-gray-600">
            Join Tampa Bay's premium contractor network. Just 5 quick fields to get started.
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
          {/* Form Header */}
          <div className="bg-orange-500 px-6 py-4">
            <h3 className="text-xl font-semibold text-white text-center">
              Contact Information
            </h3>
            <p className="text-orange-100 text-center text-sm mt-1">
              All fields are required
            </p>
          </div>

          {/* Error Display */}
          {submitError && (
            <div className="bg-red-50 border-b border-red-200 text-red-700 px-6 py-4">
              <div className="flex items-center">
                <Icon name="AlertCircle" size={16} className="mr-2" />
                <span>{submitError}</span>
              </div>
            </div>
          )}

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Field 1: Full Name */}
            <Input
              label="Full Name"
              type="text"
              placeholder="Enter your full name"
              value={formData?.fullName}
              onChange={(e) => handleInputChange('fullName', e?.target?.value)}
              required
              className="w-full"
              disabled={isSubmitting}
            />

            {/* Field 2: Email */}
            <Input
              label="Email Address"
              type="email"
              placeholder="your.email@example.com"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              required
              className="w-full"
              disabled={isSubmitting}
            />

            {/* Field 3: Phone */}
            <Input
              label="Phone Number"
              type="tel"
              placeholder="(813) 555-0123"
              value={formData?.phone}
              onChange={(e) => handleInputChange('phone', e?.target?.value)}
              required
              className="w-full"
              disabled={isSubmitting}
            />

            {/* Field 4: Company Name */}
            <Input
              label="Company Name"
              type="text"
              placeholder="Your Company LLC"
              value={formData?.companyName}
              onChange={(e) => handleInputChange('companyName', e?.target?.value)}
              required
              className="w-full"
              disabled={isSubmitting}
            />

            {/* Field 5: Trade Type */}
            <Select
              label="Primary Trade Type"
              placeholder="Select your trade specialty"
              options={tradeOptions}
              value={formData?.tradeType}
              onChange={(value) => handleInputChange('tradeType', value)}
              required
              disabled={isSubmitting}
            />

            {/* Submit Button */}
            <Button
              type="submit"
              variant="default"
              size="lg"
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 text-lg"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <Icon name="Loader2" size={20} className="mr-2 animate-spin" />
                  Submitting...
                </div>
              ) : (
                'Submit Information'
              )}
            </Button>

            {/* Trust Indicators */}
            <div className="text-center text-sm text-gray-500 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-center space-x-6">
                <div className="flex items-center">
                  <Icon name="Shield" size={16} className="mr-1 text-green-500" />
                  <span>Secure</span>
                </div>
                <div className="flex items-center">
                  <Icon name="Clock" size={16} className="mr-1 text-blue-500" />
                  <span>24h Response</span>
                </div>
                <div className="flex items-center">
                  <Icon name="Users" size={16} className="mr-1 text-orange-500" />
                  <span>No Spam</span>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Additional Info */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            By submitting this form, you agree to be contacted by ProConnect about our contractor network.
            <br />
            Your information is secure and will never be shared with third parties.
          </p>
        </div>
      </div>
    </section>
  );
};

export default SimpleContactForm;